import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  buildLinkageComparisonFields,
  formatFaceSimilarity,
  piiSnapshotsEqual,
  resolveEffectiveApplicantPii,
  type LinkageComparisonField,
} from '../../lib/legacyLinkageApplicantPii';
import { LEGACY_LINKAGE_PANEL } from '../../lib/legacyLinkageCopy';
import { formatLinkageDistance, type LegacyLinkageReviewCandidate } from '../../lib/legacyLinkageDisplay';
import type { LegacyLinkagePiiSnapshot } from '../../lib/legacyLinkageDisplay';

type Props = {
  candidate: LegacyLinkageReviewCandidate;
  identityApplicantPii: LegacyLinkagePiiSnapshot | null;
  busy?: boolean;
  onBind: (powerId: string) => void;
  onMint: () => void;
};

function MatchIcon({ match }: { match: boolean | null }) {
  if (match === true) {
    return <CheckRoundedIcon fontSize="small" sx={{ color: 'success.main' }} />;
  }
  if (match === false) {
    return <CloseRoundedIcon fontSize="small" sx={{ color: 'error.main' }} />;
  }
  return <HelpOutlineRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
}

function ComparisonRow({ field }: { field: LinkageComparisonField }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(96px, 120px) 1fr 1fr 32px' },
        gap: { xs: 0.5, sm: 1.25 },
        alignItems: 'start',
        py: 0.75,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: field.emphasizeMismatch
          ? alpha(theme.palette.error.main, theme.palette.mode === 'light' ? 0.06 : 0.12)
          : undefined,
        '&:first-of-type': { borderTop: 'none', pt: 0 },
      })}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, pt: 0.25 }}>
        {field.label}
      </Typography>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontWeight: field.emphasizeMismatch ? 700 : 500,
              color: field.emphasizeMismatch ? 'error.dark' : 'text.primary',
            }}
          >
            {field.applicant}
          </Typography>
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, pt: 0.25, flexShrink: 0 }}>
            <MatchIcon match={field.match} />
          </Box>
        </Box>
        {field.hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {field.hint}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500, pt: 0.25 }}>
        {field.legacy}
      </Typography>
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', pt: 0.25 }}>
        <MatchIcon match={field.match} />
      </Box>
    </Box>
  );
}

export function LegacyLinkageCandidateCard({
  candidate,
  identityApplicantPii,
  busy,
  onBind,
  onMint,
}: Props) {
  const legacyPii = candidate.legacyPii ?? null;
  const reviewApplicant = candidate.applicantPii ?? null;
  const effectiveApplicant = resolveEffectiveApplicantPii(reviewApplicant, identityApplicantPii);
  const identityDrift =
    reviewApplicant &&
    identityApplicantPii &&
    !piiSnapshotsEqual(reviewApplicant, identityApplicantPii);

  const fields = buildLinkageComparisonFields(effectiveApplicant, legacyPii);

  return (
    <Box
      sx={(theme) => ({
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        width: '100%',
        boxShadow:
          theme.palette.mode === 'light' ? '0 1px 2px rgba(15, 23, 42, 0.06)' : 'none',
      })}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Candidate #{candidate.rank}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
            {LEGACY_LINKAGE_PANEL.legacyCustomerIdLabel} {candidate.powerId}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Face similarity: {formatFaceSimilarity(candidate.distance)}
          {formatLinkageDistance(candidate.distance)
            ? ` · distance ${formatLinkageDistance(candidate.distance)}`
            : ''}
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 1.5 }}>
        {identityDrift ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            Comparison uses the latest identity verification values when the resolve snapshot differs.
          </Typography>
        ) : null}

        <Box
          sx={{
            display: { xs: 'none', sm: 'grid' },
            gridTemplateColumns: 'minmax(96px, 120px) 1fr 1fr 32px',
            gap: 1.25,
            mb: 0.5,
          }}
        >
          <Box />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 800, letterSpacing: '0.04em' }}
          >
            {LEGACY_LINKAGE_PANEL.columnApplicant.toUpperCase()}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 800, letterSpacing: '0.04em' }}
          >
            {LEGACY_LINKAGE_PANEL.columnLegacy.toUpperCase()}
          </Typography>
          <Box />
        </Box>

        {fields.map((field) => (
          <ComparisonRow key={field.label} field={field} />
        ))}

        <Divider sx={{ my: 1.5 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => onBind(candidate.powerId)}
            sx={{ minWidth: { sm: 200 }, fontWeight: 700 }}
          >
            {LEGACY_LINKAGE_PANEL.confirmLink(candidate.powerId)}
          </Button>
          <Button
            variant="text"
            color="inherit"
            disabled={busy}
            onClick={onMint}
            sx={{ textTransform: 'none' }}
          >
            {LEGACY_LINKAGE_PANEL.notThisPerson}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
